import { type JSX, memo, useCallback, useMemo, useRef } from 'react';
import { cx } from '@linaria/core';

import { BestOddsIcon } from '@cx-modules/ui';

import { useOddsGridContext } from '../../context/OddsGridContext';
import { useBetslipOptimized } from '../../hooks/useBetslipOptimized';
import { useConditionalFlash } from '../../hooks/useConditionalFlash';
import { useSelector } from '../../providers/StoreProvider';
import { type Bet, type Odd, oddsShownType } from '../../types';
import { BookmakerCell } from '../BookmakerCell';

import {
  bestOddsStyles,
  betTooltip,
  DesktopOnlyInline,
  EachWayTermsOddsCell,
  EmptyOddCell,
  noStreamerAnimation,
  oddDrifting,
  OddsCellDesktop,
  OddsCellMobile,
  OddsCellWrapper,
  oddShortening,
  OddsRow,
  oddStable,
  oddUnsupported,
  selectedBet,
} from './style';

interface OddsGridCellProps {
  bet: Bet;
  betName?: string;
  bookieCode: string;
  isBest: boolean;
  odds: Odd | undefined;
  oddsFormat?: string;
  oddsText: string | number | undefined;
}

const OddsGridCellComponent = ({
  bet,
  betName,
  bookieCode,
  isBest,
  odds,
  oddsFormat,
  oddsText,
}: OddsGridCellProps): JSX.Element | null => {
  const { oddsShown } = useOddsGridContext();

  const { allowOddsClickout, disableStreamer, onOddsButtonClick } = useSelector(store => ({
    allowOddsClickout: store.allowOddsClickout,
    disableStreamer: store.disableStreamer,
    onOddsButtonClick: store.onOddsButtonClick,
  }));

  const nonClickable = oddsShown === oddsShownType.OC_NON_CLICKABLE;

  const inOut = odds?.movement || '';
  const name = betName ?? bet?.name;

  const drifterSteamerClass = useMemo(() => {
    if (!allowOddsClickout) {
      return oddUnsupported;
    }

    // Apply movement-based colors even when streamer is disabled
    if (inOut.toLowerCase() === 'drifting') {
      return oddDrifting;
    }
    if (inOut.toLowerCase() === 'shortening') {
      return oddShortening;
    }
    return oddStable;
  }, [inOut, allowOddsClickout]);

  const cellRef = useRef<HTMLButtonElement | null>(null);

  // Use conditional flash hook that can be disabled
  useConditionalFlash({
    disabled: disableStreamer,
    ref: cellRef,
    trigger: oddsText,
  });

  const { isBookieBetSelected, toggleBetslipBet } = useBetslipOptimized({
    bet,
    betId: bet.id,
    bookieCode,
    odds,
    oddsText,
  });
  const onClickHandler = useCallback(() => {
    if (!allowOddsClickout) return;
    if (onOddsButtonClick) {
      return onOddsButtonClick('odds_grid_click', bet.id, bookieCode, name, oddsText);
    }
    toggleBetslipBet();
  }, [bet.id, bookieCode, name, oddsText, onOddsButtonClick, toggleBetslipBet, allowOddsClickout]);

  return (
    <>
      {/* Desktop variant */}
      <DesktopOnlyInline>
        {typeof oddsText === 'undefined' ? (
          <EmptyOddCell />
        ) : (
          <OddsCellWrapper className={cx(drifterSteamerClass)}>
            <OddsCellDesktop
              aria-label="Add to betslip"
              className={cx(
                isBookieBetSelected ? selectedBet : '',
                allowOddsClickout && betTooltip,
                !isBookieBetSelected && isBest && allowOddsClickout && bestOddsStyles,
                disableStreamer && noStreamerAnimation,
              )}
              data-bk={bookieCode}
              data-testid="odds-cell"
              disabled={nonClickable}
              oddsFormat={oddsFormat || 'decimal'}
              onClick={onClickHandler}
              ref={cellRef}
            >
              {oddsText}
            </OddsCellDesktop>
          </OddsCellWrapper>
        )}
      </DesktopOnlyInline>

      {/* Mobile variant - OddsRow has responsive CSS built in */}
      {typeof oddsText !== 'undefined' && (
        <OddsRow>
          <BookmakerCell bookieCode={bookieCode} />
          <EachWayTermsOddsCell>
            {isBest && allowOddsClickout && (
              <span className={bestOddsStyles}>
                <BestOddsIcon />
              </span>
            )}
            <OddsCellMobile
              className={cx(
                isBookieBetSelected ? selectedBet : drifterSteamerClass,
                disableStreamer && noStreamerAnimation,
              )}
              data-bk={bookieCode}
              data-testid="odds-cell"
              onClick={onClickHandler}
              ref={cellRef}
            >
              {oddsText}
            </OddsCellMobile>
          </EachWayTermsOddsCell>
        </OddsRow>
      )}
    </>
  );
};

export const OddsGridCell = memo(OddsGridCellComponent);
